import { useState } from "react";
import { Link } from "react-router-dom";
import api, { formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { ArrowLeft, EnvelopeSimple, ShieldCheck, Key } from "@phosphor-icons/react";
import { toast } from "sonner";

export default function ForgotPasswordPage() {
  const [step, setStep] = useState("email"); // email | code | done
  const [email, setEmail] = useState("");
  const [code, setCode] = useState("");
  const [displayedCode, setDisplayedCode] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const handleRequestCode = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      const { data } = await api.post("/auth/forgot-password", { email });
      if (data.code) {
        setDisplayedCode(data.code);
      }
      setStep("code");
      toast.success("Reset code generated");
    } catch (err) {
      setError(formatApiError(err.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  };

  const handleResetPassword = async (e) => {
    e.preventDefault();
    setError("");
    if (newPassword.length < 6) { setError("Password must be at least 6 characters"); return; }
    if (newPassword !== confirmPassword) { setError("Passwords do not match"); return; }
    setLoading(true);
    try {
      await api.post("/auth/reset-password", { email, code, new_password: newPassword });
      setStep("done");
      toast.success("Password reset successfully");
    } catch (err) {
      setError(formatApiError(err.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#F9FAFB] flex items-center justify-center p-4">
      <div className="w-full max-w-[400px] animate-fade-in">
        {/* Logo */}
        <div className="text-center mb-8">
          <div className="inline-flex items-center justify-center w-14 h-14 rounded-xl bg-[#C5A059] text-white text-xl font-extrabold mb-4" style={{ fontFamily: "'Cabinet Grotesk', sans-serif" }}>BM</div>
          <h1 className="text-2xl font-bold text-[#0F172A]" style={{ fontFamily: "'Cabinet Grotesk', sans-serif" }}>Reset Password</h1>
          <p className="text-xs text-[#6B7280] uppercase tracking-[0.15em] mt-1 font-medium">Recover your account</p>
        </div>

        <div className="bg-white rounded-lg border border-[#E5E7EB] p-6 md:p-8">
          {error && (
            <div className="mb-4 p-3 rounded-lg bg-red-50 border border-red-200 text-sm text-red-700" data-testid="forgot-error">{error}</div>
          )}

          {/* STEP 1: Enter email */}
          {step === "email" && (
            <>
              <div className="flex items-center gap-3 mb-4">
                <div className="w-10 h-10 rounded-lg bg-[#1E3A8A]/10 flex items-center justify-center">
                  <EnvelopeSimple size={20} weight="duotone" className="text-[#1E3A8A]" />
                </div>
                <div>
                  <h2 className="text-base font-semibold text-[#0F172A]">Enter your email</h2>
                  <p className="text-xs text-[#6B7280]">We'll generate a reset code for you</p>
                </div>
              </div>
              <form onSubmit={handleRequestCode} className="space-y-4">
                <div>
                  <Label htmlFor="email" className="text-[#0F172A] text-sm font-medium">Email Address</Label>
                  <Input
                    id="email" type="email" placeholder="you@company.com"
                    value={email} onChange={(e) => setEmail(e.target.value)} required
                    className="mt-1.5 h-11 border-[#E5E7EB]"
                    data-testid="forgot-email-input"
                  />
                </div>
                <Button type="submit" disabled={loading} className="w-full h-11 bg-[#0F172A] hover:bg-[#1E293B]" data-testid="forgot-submit-btn">
                  {loading ? "Sending..." : "Get Reset Code"}
                </Button>
              </form>
            </>
          )}

          {/* STEP 2: Enter code + new password */}
          {step === "code" && (
            <>
              <div className="flex items-center gap-3 mb-4">
                <div className="w-10 h-10 rounded-lg bg-[#C5A059]/10 flex items-center justify-center">
                  <ShieldCheck size={20} weight="duotone" className="text-[#C5A059]" />
                </div>
                <div>
                  <h2 className="text-base font-semibold text-[#0F172A]">Enter reset code</h2>
                  <p className="text-xs text-[#6B7280]">Check below for your code, then set a new password</p>
                </div>
              </div>

              {/* Display the code - mock email delivery */}
              {displayedCode && (
                <div className="mb-4 p-4 rounded-lg bg-[#1E3A8A]/5 border border-[#1E3A8A]/20" data-testid="reset-code-display">
                  <p className="text-xs text-[#1E3A8A] font-medium mb-1">Your reset code (normally sent via email):</p>
                  <p className="text-2xl font-bold text-[#1E3A8A] tracking-[0.2em] font-mono">{displayedCode}</p>
                  <p className="text-[10px] text-[#6B7280] mt-1">Expires in 15 minutes</p>
                </div>
              )}

              <form onSubmit={handleResetPassword} className="space-y-4">
                <div>
                  <Label className="text-[#0F172A] text-sm font-medium">Reset Code</Label>
                  <Input
                    type="text" placeholder="Enter 6-digit code"
                    value={code} onChange={(e) => setCode(e.target.value)} required
                    className="mt-1.5 h-11 border-[#E5E7EB] font-mono tracking-widest text-center text-lg"
                    data-testid="reset-code-input"
                  />
                </div>
                <div>
                  <Label className="text-[#0F172A] text-sm font-medium">New Password</Label>
                  <Input
                    type="password" placeholder="Min 6 characters"
                    value={newPassword} onChange={(e) => setNewPassword(e.target.value)} required
                    className="mt-1.5 h-11 border-[#E5E7EB]"
                    data-testid="reset-new-password"
                  />
                </div>
                <div>
                  <Label className="text-[#0F172A] text-sm font-medium">Confirm Password</Label>
                  <Input
                    type="password" placeholder="Re-enter new password"
                    value={confirmPassword} onChange={(e) => setConfirmPassword(e.target.value)} required
                    className="mt-1.5 h-11 border-[#E5E7EB]"
                    data-testid="reset-confirm-password"
                  />
                </div>
                <Button type="submit" disabled={loading} className="w-full h-11 bg-[#0F172A] hover:bg-[#1E293B]" data-testid="reset-submit-btn">
                  {loading ? "Resetting..." : "Reset Password"}
                </Button>
              </form>
            </>
          )}

          {/* STEP 3: Success */}
          {step === "done" && (
            <div className="text-center py-4">
              <div className="w-14 h-14 rounded-full bg-green-100 flex items-center justify-center mx-auto mb-4">
                <Key size={24} weight="duotone" className="text-green-600" />
              </div>
              <h2 className="text-lg font-semibold text-[#0F172A] mb-2">Password Reset</h2>
              <p className="text-sm text-[#6B7280] mb-6">Your password has been successfully reset. You can now sign in with your new password.</p>
              <Link to="/hub/login">
                <Button className="w-full h-11 bg-[#0F172A] hover:bg-[#1E293B]" data-testid="back-to-login-btn">
                  Sign In
                </Button>
              </Link>
            </div>
          )}
        </div>

        <p className="text-center text-sm text-[#6B7280] mt-6">
          <Link to="/hub/login" className="text-[#1E3A8A] font-medium hover:underline inline-flex items-center gap-1" data-testid="back-to-login-link">
            <ArrowLeft size={14} /> Back to Sign In
          </Link>
        </p>
      </div>
    </div>
  );
}
